import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { FormikDropdownField, FormikTextField } from '@components/FormikFields';
import InputLabel from '@components/InputLabel';

import {
  FORM_UNITS,
  UNKNOWN_UNIT,
  unitOptions,
} from '@constants/medication-dosage';

import type { DosageStep } from '@utils/fhir-mappers/dosageSteps';
import type { MedicationIngredientRow } from '@utils/fhir-mappers/medicationIngredients';

import {
  DynamicNoun,
  FieldCell,
  FieldRow,
  RequiredBadge,
  RowHelp,
  RowWarning,
  SectionBlock,
  SectionBody,
  SectionError,
  SectionHeader,
  SectionHint,
  SectionTitle,
  SentenceCell,
  SentenceErrors,
  RangeButton,
  SentenceRow,
  SubsectionLabel,
} from './Fields.style';
import Ingredients from './Ingredients';
import { AndOrDivider, FormPicker } from './MedicineAmount.style';
import { useDoseFormNoun } from './useDoseFormNoun';
import { useSentenceErrors } from './useSentenceErrors';

const messages = defineMessages({
  form: { id: 'medicationAmount.form', defaultMessage: 'Form' },
  formHelp: {
    id: 'medicationAmount.formHelp',
    defaultMessage: 'Sets the units for the strength and the dose below.',
  },
  /**
   * A record can arrive holding a word this list doesn't offer — a legacy dose
   * of "10 mg", or an EMR `Medication.form` of "suspension". The value is kept
   * and the save is allowed; the curator is told so they can correct it.
   */
  formUnrecognised: {
    id: 'medicationAmount.formUnrecognised',
    defaultMessage:
      'This record uses "{form}", which is not one of the forms above. It will be saved as it is until you pick one.',
  },
  title: { id: 'medicationAmount.title', defaultMessage: 'How much' },
  badge: { id: 'medicationAmount.badge', defaultMessage: 'At least one' },
  hint: {
    id: 'medicationAmount.hint',
    defaultMessage: 'Strength, dose, or both.',
  },
  andOr: { id: 'medicationAmount.andOr', defaultMessage: 'and / or' },
  dose: { id: 'medicationAmount.dose', defaultMessage: 'Dose' },
  /** The dose row as one sentence: "Takes 1 tablet". */
  doseSentence: {
    id: 'medicationAmount.doseSentence',
    defaultMessage: 'Takes {low}{form}',
  },
  /**
   * The range form. Held back until there is a low bound, so a blank row reads
   * "Takes ___ tablets" rather than "Takes ___ to ___ tablets" with a dangling
   * "to" and an unlabelled empty box.
   */
  doseSentenceRange: {
    id: 'medicationAmount.doseSentenceRange',
    defaultMessage: 'Takes {low} to {high}{form}',
  },
  doseAmount: { id: 'medicationDosage.doseAmount', defaultMessage: 'Quantity' },
  doseTo: { id: 'medicationDosage.doseTo', defaultMessage: 'Quantity to' },
  addRange: {
    id: 'medicationAmount.addRange',
    defaultMessage: 'or a range',
  },
  removeRange: {
    id: 'medicationAmount.removeRange',
    defaultMessage: 'remove range',
  },
  none: { id: 'medicationAmount.none', defaultMessage: '—' },
});

interface MedicineAmountProps {
  isEditing: boolean;
}

interface AmountValues {
  doseForm?: string;
  dosageSteps?: DosageStep[];
  ingredients?: MedicationIngredientRow[];
}

/**
 * Strength and dose, boxed together as one "at least one" requirement, with the
 * Form picker that supplies the noun for both (VEG-760).
 *
 * The two used to be separate sections, each with its own unit dropdown — and a
 * combination product put one on every strength row, so a two-part tablet asked
 * for the word "tablet" three times. Nothing said the record needed one of them
 * either. Now the Form is asked once, above the block, and every line below
 * reads back as a sentence in that noun.
 *
 * Read mode is unchanged: the strength summary and the dosage sentence say it
 * all, and a box round them would be chrome with nothing to hold.
 */
export default function MedicineAmount({ isEditing }: MedicineAmountProps) {
  const { formatMessage } = useIntl();
  const [openRange, setOpenRange] = useState(false);
  const doseFormNoun = useDoseFormNoun();
  const doseErrors = useSentenceErrors([
    'dosageSteps.0.doseValue',
    'dosageSteps.0.doseValueHigh',
  ]);
  const { values, setFieldValue, errors, submitCount } =
    useFormikContext<AmountValues>();

  const doseForm = values.doseForm ?? '';
  const step = values.dosageSteps?.[0];

  /**
   * FHIR stores the noun twice — on `doseQuantity.unit` and on every strength
   * denominator — so picking a Form has to write it to both.
   *
   * Only on an actual pick. This used to run on mount too, to reconcile records
   * whose `Medication.form` and `doseQuantity.unit` disagree, and that quietly
   * rewrote stored values on a full PUT with nobody touching anything: a
   * "250 mg per 5 mL" suspension saved as "per 5 suspension", and a 10 mg dose
   * under a form of "tablet" saved as 10 tablets. VEG-760 asks for the
   * opposite — opening a 10 mg record and saving it leaves the unit alone — so
   * a disagreement is now shown rather than resolved, and `inferDoseForm` seeds
   * from the dose the record actually stores.
   */
  const applyForm = (value: string) => {
    // `unknown` is a real dose unit — it is how "we don't know" is stored — but
    // it is not a denominator: "875 mg per 1 unknown" says less than the bare
    // count the mapper falls back to.
    const perUnit = value === UNKNOWN_UNIT ? '' : value;
    setFieldValue('dosageSteps.0.doseUnit', value);
    // The ceiling counts the same thing the dose does ("max 8 tablets per
    // day"), so it takes the noun too. `unknown` is left off it: the spec has
    // no unknown on a ceiling, and a limit nobody can put a unit on is not one.
    setFieldValue('dosageSteps.0.maxDoseUnit', perUnit);
    (values.ingredients ?? []).forEach((_row, index) => {
      setFieldValue(`ingredients.${index}.strengthPerUnit`, perUnit);
    });
  };

  if (!isEditing) {
    return <Ingredients isEditing={false} />;
  }

  // Yup attaches the at-least-one test to the ingredients array, so its message
  // arrives as a string on the array rather than on any one field.
  const blockError =
    typeof errors.ingredients === 'string' ? errors.ingredients : '';

  // A number input makes Formik coerce the value to a JS number, but DosageStep
  // fields are strings (matching how dosageToSteps hydrates them).
  const keepString = (name: string) => (value: number | string) =>
    setFieldValue(name, String(value));

  /**
   * Whether the value on the record is a form this list knows. Kept selectable
   * by `unitOptions`, so the save carries it through untouched — the warning is
   * there because a word like "mg" or "suspension" in this field means the
   * strength and dose sentences below are reading in something that isn't a
   * dose form.
   */
  const unrecognisedForm = Boolean(
    doseForm && !FORM_UNITS.some((unit) => unit.value === doseForm)
  );

  /**
   * The upper bound is opt-in. Rendering it unconditionally left a dangling
   * "to ___" after every single dose, which is what a stored dose is most of
   * the time. A record that already holds a range shows it without asking.
   */
  const showRange = openRange || Boolean(step?.doseValueHigh);

  /**
   * Going back to a single dose has to clear the upper bound as well as close
   * the input. `showRange` reads the stored value too, so leaving it behind
   * would reopen the range on the next render — and a value nobody can see
   * would still be saved.
   */
  const closeRange = () => {
    setOpenRange(false);
    setFieldValue('dosageSteps.0.doseValueHigh', '');
  };

  const doseNoun = doseFormNoun(
    doseForm,
    Number(step?.doseValueHigh || step?.doseValue || 1)
  );

  return (
    <>
      <FormPicker>
        <FieldRow>
          <FieldCell $size="text">
            <FormikDropdownField
              label={formatMessage(messages.form)}
              name="doseForm"
              onChange={applyForm}
              options={[
                { k: '', v: formatMessage(messages.none) },
                ...unitOptions(FORM_UNITS, doseForm),
              ]}
            />
          </FieldCell>
        </FieldRow>
        {unrecognisedForm ? (
          <RowWarning data-testid="dose-form-warning">
            {formatMessage(messages.formUnrecognised, { form: doseForm })}
          </RowWarning>
        ) : (
          <RowHelp>{formatMessage(messages.formHelp)}</RowHelp>
        )}
      </FormPicker>

      <SectionBlock data-testid="medicine-amount">
        <SectionHeader>
          <SectionTitle>{formatMessage(messages.title)}</SectionTitle>
          <RequiredBadge>{formatMessage(messages.badge)}</RequiredBadge>
          <SectionHint>{formatMessage(messages.hint)}</SectionHint>
        </SectionHeader>
        <SectionBody>
          {blockError && submitCount > 0 && (
            <SectionError role="alert" data-testid="medicine-amount-error">
              {blockError}
            </SectionError>
          )}

          <Ingredients isEditing />

          <AndOrDivider>{formatMessage(messages.andOr)}</AndOrDivider>

          <SubsectionLabel>
            <InputLabel label={formatMessage(messages.dose)} />
          </SubsectionLabel>
          <SentenceRow data-testid="dose-row">
            <FormattedMessage
              {...(showRange
                ? messages.doseSentenceRange
                : messages.doseSentence)}
              values={{
                form: <DynamicNoun key="form">{doseNoun}</DynamicNoun>,
                low: (
                  <SentenceCell $size="number" key="low">
                    <FormikTextField
                      type="number"
                      label={formatMessage(messages.doseAmount)}
                      name="dosageSteps.0.doseValue"
                      onChange={keepString('dosageSteps.0.doseValue')}
                    />
                  </SentenceCell>
                ),
                high: showRange ? (
                  <SentenceCell $size="number" key="high">
                    <FormikTextField
                      type="number"
                      label={formatMessage(messages.doseTo)}
                      name="dosageSteps.0.doseValueHigh"
                      onChange={keepString('dosageSteps.0.doseValueHigh')}
                    />
                  </SentenceCell>
                ) : null,
              }}
            />
            {showRange ? (
              <RangeButton
                data-testid="dose-remove-range"
                onClick={closeRange}
                type="button"
              >
                {formatMessage(messages.removeRange)}
              </RangeButton>
            ) : (
              <RangeButton
                data-testid="dose-add-range"
                onClick={() => setOpenRange(true)}
                type="button"
              >
                {formatMessage(messages.addRange)}
              </RangeButton>
            )}
          </SentenceRow>
          {doseErrors.length > 0 && (
            <SentenceErrors role="alert" data-testid="dose-row-errors">
              {doseErrors.join(' ')}
            </SentenceErrors>
          )}
        </SectionBody>
      </SectionBlock>
    </>
  );
}
